import { Job } from './job';

export interface ResultColorMap {
  color_map: string;
  min_value: number;
  max_value: number;
}

export interface NormalizationResult {
  metadata: {};
  channel_normalization_results: Array<{
    params: {
      max_c: number;
      min_c: number;
    };
    channel_index: number;
    derivation_method?: NormalizationDerivationMethod;
    normalization_type: string;
  }>;
}

export type NormalizationDerivationMethod = 'manual' | 'legacy' | 'autonormalization';

export interface PresentationInfoLayer {
  file_type?: 'pmt' | 'geojson' | 'parquet';
  class_name: string;
  storage_url: string;
  artifact_url: string;
  key?: string;
  // Legacy
  heatmap_url?: string;
}

export interface OptionsEntry {
  key: string | number;
  color?: string | ResultColorMap;
  column_name?: string;
  multilayer?: boolean;
  layers?: OptionsEntry[];
  with_composite?: boolean;
  min?: number;
  max?: number;
  type?:
    | 'uuid' // i.e. the id of a cell
    | 'point' // i.e. X, Y coordinates
    | 'boolean' // i.e. marker positivity
    | 'categorical' // i.e. label
    | 'categorical_array' // i.e. polygons that include this point
    | 'integer' // i.e. number of nuclei
    | 'int'
    | 'float'; // i.e. intensity
}

export interface ExperimentResult {
  approved: boolean | null;
  internallyApproved: boolean | null;
  experimentResultId: number;
  flowClassName: FlowClassName;
  name?: string;
  options?: OptionsEntry[];
  resultType?: ResultType;
  description?: string;
  orchestrationId: string;
  primaryRunOrchestrationId?: string;
  presentationInfo: { [key: string]: any };
  numFeatures?: number;
  features?: Features;
  featureValues?: any;
  studyId: string;
  slideId: string;
  createdAt: string;
  createdBy?: string;
  deletedAt?: string;
  deletedBy?: string;
  qaFailed?: boolean;
  job?: Job;
}

export enum FlowClassName {
  CalculateFeatures = 'CalculateFeatures',
  ConcurrentInferenceFlow = 'ConcurrentInferenceFlow',
  ConcurrentClassifyCellsFlow = 'ConcurrentClassifyCellsFlow',
  DeconvSlideProcessor = 'DeconvSlideProcessor',
  FlexibleInferenceFlow = 'FlexibleInferenceFlow',
  CreateMultiplexHistogramFlow = 'CreateMultiplexHistogramFlow',
  CellTypingFlow = 'CellTypingFlow',
  NuclearSegmentationFlow = 'NuclearSegmentationFlow',
  MultiplexCellSegmentationProcessor = 'MultiplexCellSegmentationProcessor',
  NormalizeMultiplexSlideFlow = 'NormalizeMultiplexSlideFlow',
  PerformRegistrationFlow = 'PerformRegistrationFlow',
  External = 'External',
  GeneralReportFlow = 'GeneralReportFlow',
}

export const bulkPublishableFlowClasses: FlowClassName[] = [FlowClassName.CalculateFeatures, FlowClassName.External];

export const publishableFlowClasses: FlowClassName[] = [
  FlowClassName.CalculateFeatures,
  FlowClassName.NormalizeMultiplexSlideFlow,
  FlowClassName.NuclearSegmentationFlow,
  FlowClassName.DeconvSlideProcessor,
  FlowClassName.CreateMultiplexHistogramFlow,
  FlowClassName.External,
  FlowClassName.CellTypingFlow,
];

export enum ResultType {
  PipsCellsTable = 'pips_cells_table',
  MarkersProbabilities = 'slide_inference_multiplex_markers_probabilities',
  SlideInferenceNucleiSegmentation = 'slide_inference_nuclei',
  SlideInferenceCellSegmentation = 'slide_inference_cell_segmentation',
  SlideInferenceAreas = 'slide_inference_areas',
  SlideInferencePoints = 'slide_inference_points',
  SlideInferenceDefects = 'slide_inference_defects',
  SlideInferenceTls = 'slide_inference_tls',
  SlideInferenceClassificationIntensity = 'slide_inference_classification_intensity',
  SlideInferenceClassification = 'slide_inference_classification',
  SlideCellAttributesTable = 'slide_cell_attributes_table',
  SlideInferenceCellTyping = 'slide_inference_multiplex_cell_typing',
  Normalization = 'normalization',
  MultiplexThresholds = 'multiplex_thresholds',
  CellQuantification = 'cell_quantification',
  SlideInferenceTsm = 'slide_inference_tsm',
  AreaQuantification = 'area_quantification',
  SlideSegmentationsTable = 'slide_segmentations_table',
  PipsAreaInstancesTable = 'pips_area_instances_table',
  SlideDetectionsTable = 'slide_detections_table',
}

export const publishableResultTypes: string[] = [ResultType.Normalization];

export interface ExperimentResultUpdate {
  approved?: boolean;
  internallyApproved?: boolean;
}

export interface SlideResultsSummaryData {
  slideId: string;
  caseId: number;
  caseName: string;
  flowClassName: FlowClassName;
  publishedOrchestrationId?: string;
}

export interface Features {
  [key: string]: number | string | Array<number | null | undefined>;
}

export interface Feature {
  key: string;
  value: number | string;
}

export enum ResultsMode {
  Manual = 'manual',
  InternallyApproved = 'internally_approved',
  Published = 'published',
  All = 'all',
}

export interface ResultsExistence {
  // flowClassName
  [key: string]: {
    // slideId
    [key: string]: boolean;
  };
}

export enum ArtifactType {
  GenericPipsTableParquet = 'generic_table.pips_cells_table.parquet',
}
